<template>
  <!-- Table Container Card -->
  <b-card>

    <validation-observer ref="profitAndLossRules">
      <b-form
        @submit.prevent="handleSubmit"
      >
        <b-row class="match-height">
          <!-- Branch -->
          <b-col md="2">
            <SelectField
              id="branch"
              v-model="profitOrLossFormData.branch"
              :label="$t('Branch')"
              :options="branchOptions"
              :reduce="option => option"
              :error-name="$t('Branch')"
              item="label"
              :placeholder="$t('Select')"
            />
          </b-col>
          <!-- Branch -->

          <!-- Level -->
          <b-col md="2">
            <SelectField
              id="level"
              v-model="profitOrLossFormData.level"
              :label="$t('Level')"
              :options="translatedLevelOptions"
              :reduce="option => option.value"
              :error-name="$t('Level')"
              rules="required"
              item="label"
              :placeholder="$t('Select')"
            />
          </b-col>
          <!-- Level -->
          <b-col
            md="2"
            class="mt-2 current-year"
          >
            <SwitchButton
              v-model="profitOrLossFormData.isCurrentFinancialDate"
              :label="$t('Use Current Financial Date')"
              name="use-current-financial-date"
              :readonly="isReadonly"
              @input="setUseCurrentFinancialDate(profitOrLossFormData.isCurrentFinancialDate)"
            />
          </b-col>
          <b-col
            md="2"
            class="mt-2 current-year"
          >
            <SwitchButton
              v-model="profitOrLossFormData.ignoreZeroBalances"
              :label="$t('Ignore Zero Balances?')"
              name="ignore-zero-balances"
              :readonly="isReadonly"
            />
          </b-col>

          <!-- Financial Date From-->
          <b-col md="2">
            <DatePickerField
              id="financial-date-from"
              v-model="profitOrLossFormData.financialDateFrom"
              :min-date="new Date()"
              :label="$t('Financial Date From')"
              :error-name="$t('Financial Date From')"
              :config="datePickerConfig"
              :placeholder="$t('Select Date')"
              rules="required"
              :disabled="isDisabledText"
            />
          </b-col>
          <!--  Financial Date From -->

          <!-- Financial Date To-->
          <b-col md="2">
            <DatePickerField
              id="financial-date-to"
              v-model="profitOrLossFormData.financialDateTo"
              :min-date="new Date()"
              :label="$t('Financial Date To')"
              :error-name="$t('Financial Date To')"
              :config="datePickerConfig"
              :placeholder="$t('Select Date')"
              rules="required"
              :disabled="isDisabledText"
            />
          </b-col>
          <!--  Financial Date To -->

          <b-col
            md="12"
            class="mt-0"
            style="text-align: right;"
          >
            <Button
              :label="$t('Generate Report')"
              :loading="isLoading"
              :disabled="isDisabled"
              variant="primary"
              size="md"
              @click="handleSubmit"
            />
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        />
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-dropdown
              v-if="reportData.length && getUserPermission('EXPORT_PROFIT_OR_LOSS_REPORT')"
              id="dropdown-1"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              text="Export"
              variant="primary"
            >
              <b-dropdown-item @click="csvExport(csvData)">
                {{ $t('CSV') }}
              </b-dropdown-item>
              <b-dropdown-item @click="excelExport(csvData)">
                {{ $t('Excel') }}
              </b-dropdown-item>
              <b-dropdown-item @click="pdfExport">
                {{ $t('PDF') }}
              </b-dropdown-item>
              <b-dropdown-item @click="printData">
                {{ $t('Print') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-col>

      </b-row>

    </div>

    <div id="profit-loss-report-print">
      <b-card
        id="prints"
        class="header-position"
      >
        <h2>{{ userDetails.institutionName }}</h2>
        <h4> Profit or Loss Report</h4>
        <h5>{{ `Date Rage ${(profitOrLossFormData.isCurrentFinancialDate) ? 'All' : `${profitOrLossFormData.financialDateFrom} - ${profitOrLossFormData.financialDateTo}` }` }}</h5>
        <h5>{{ profitOrLossFormData.branch.label }} - {{ profitOrLossFormData.branch.value }}</h5>
        <h5> {{ $t('Level') }} {{ profitOrLossFormData.level }}</h5>
      </b-card>
      <b-table
        v-if="reportData.length"
        ref="profitAndLossTableRef"
        :items="reportData"
        :busy="isBusy"
        :per-page="3000"
        :current-page="currentPage"
        responsive
        :fields="translatedTableHeaders"
        primary-key="id"
        show-empty
        :empty-text="$t('No records available')"
        class="position-relative"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>

      </b-table>
    </div>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BForm, BCol, BTable, BSpinner, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import 'jspdf-autotable'
import * as FileSaver from 'file-saver'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toBalance } from '@/helpers/formatAmount'
import { toYearWithDate, toCustomDate } from '@/helpers/formatDate'
import { reportDataDetail } from '../../../../helpers/data'
import SwitchButton from '../../../components/switch/Switch.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import Button from '../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BTable,
    BDropdown,
    BForm,
    Button,
    BDropdownItem,
    SelectField,
    SwitchButton,
    DatePickerField,
    ValidationObserver,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isBusy: false,
      isDisabledText: false,
      tableHeaders: [
        { key: 'code', label: 'Code' },
        { key: 'category', label: 'Category' },
        { key: 'amount', label: 'Amount' },
        { key: 'totalAmount', label: 'Total Amount' },
      ],
      levelOptions: [
        { label: 'Level 1', value: 1 },
        { label: 'Level 2', value: 2 },
        { label: 'Level 3', value: 3 },
        { label: 'Level 4', value: 4 },
      ],
      required,
      isLoading: false,
      isDisabled: false,
      showSpinner: false,
      reportData: [],
      reportInfo: reportDataDetail,
      profitOrLossFormData: {
        branch: '',
        level: this.$t('Level 1'),
        financialDateFrom: null,
        financialDateTo: null,
        isCurrentFinancialDate: true,
        ignoreZeroBalances: true,
      },
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        defaultDate: '',
      },
    }
  },
  computed: {
    csvData() {
      return this.reportData.map(item => ({
        Code: item.debit,
        Category: item.credit,
        Amount: item.amount,
        'Total Amount': item.totalAmount,
      }))
    },
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedLevelOptions() {
      return this.levelOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  methods: {
    ...mapActions({
      getProfitOrLoss: 'reports/getProfitOrLoss',
      downloadProfitAndLossReport: 'reports/downloadProfitAndLossReport',
    }),
    setUseCurrentFinancialDate(dataValue) {
      if (dataValue === false) {
        this.isDisabledText = false
        this.profitOrLossFormData.financialDateFrom = null
        this.profitOrLossFormData.financialDateTo = null
      } else {
        this.isDisabledText = true
        this.profitOrLossFormData.financialDateFrom = null
        this.profitOrLossFormData.financialDateTo = null
      }
    },
    async fetchProfitOrLoss() {
      try {
        this.showSpinner = true
        if (this.profitOrLossFormData.level === 'Level 1') {
          this.profitOrLossFormData.level = 1
        }
        const response = await this.getProfitOrLoss({
          ...this.profitOrLossFormData,
          branchCode: this.profitOrLossFormData.branch ? this.profitOrLossFormData.branch.value : '',
          financialDateFrom: this.profitOrLossFormData.financialDateFrom ? toYearWithDate(this.profitOrLossFormData.financialDateFrom) : '',
          financialDateTo: this.profitOrLossFormData.financialDateTo ? toYearWithDate(this.profitOrLossFormData.financialDateTo) : '',
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
        }
        if (status === 200 && data.data) {
          this.reportData = data.data.map(item => ({
            ...item,
            amount: toBalance(item.amount),
            totalAmount: item.totalAmount >= 0 ? toBalance(item.totalAmount) : `(${toBalance(Math.abs(item.totalAmount))})`,
          }))
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadProfitAndLossReport({
          ...this.profitOrLossFormData,
          branchCode: this.profitOrLossFormData.branch ? this.profitOrLossFormData.branch.value : '',
          financialDateFrom: this.profitOrLossFormData.financialDateFrom ? toYearWithDate(this.profitOrLossFormData.financialDateFrom) : '',
          financialDateTo: this.profitOrLossFormData.financialDateTo ? toYearWithDate(this.profitOrLossFormData.financialDateTo) : '',
          fileExportFormat: 1,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
          FileSaver.saveAs(blob, 'Profit or Loss Report.csv')
          this.alertSuccess({ text: this.$t('Your request to download Profit or Loss Report was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadProfitAndLossReport({
          ...this.profitOrLossFormData,
          branchCode: this.profitOrLossFormData.branch ? this.profitOrLossFormData.branch.value : '',
          financialDateFrom: this.profitOrLossFormData.financialDateFrom ? toYearWithDate(this.profitOrLossFormData.financialDateFrom) : '',
          financialDateTo: this.profitOrLossFormData.financialDateTo ? toYearWithDate(this.profitOrLossFormData.financialDateTo) : '',
          fileExportFormat: 0,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
          FileSaver.saveAs(blob, 'Profit or Loss Report.xlsx')
          this.alertSuccess({ text: this.$t('Your request to download Profit or Loss Report was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async pdfExport() {
      try {
        this.isBusy = true
        const response = await this.downloadProfitAndLossReport({
          ...this.profitOrLossFormData,
          branchCode: this.profitOrLossFormData.branch ? this.profitOrLossFormData.branch.value : '',
          financialDateFrom: this.profitOrLossFormData.financialDateFrom ? toYearWithDate(this.profitOrLossFormData.financialDateFrom) : '',
          financialDateTo: this.profitOrLossFormData.financialDateTo ? toYearWithDate(this.profitOrLossFormData.financialDateTo) : '',
          fileExportFormat: 2,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, 'Profit or Loss Report.pdf')
          this.alertSuccess({ text: this.$t('Your request to download Profit or Loss Report was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    printData() {
      window.print()
    },

    handleSubmit() {
      if (this.profitOrLossFormData.isCurrentFinancialDate === false) {
        this.$refs.profitAndLossRules.validate().then(async success => {
          if (success) {
            this.fetchProfitOrLoss()
          }
        })
      } else {
        this.fetchProfitOrLoss()
      }
    },
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
    @media screen {
  #prints {
    display: none;
   }
}
.current-year .custom-control-label {
  font-size: 0.7rem !important;
}

@media print {
   html, body {
     height: 99%;
     page-break-after: avoid !important;
     page-break-before: avoid !important;
     visibility:hidden;
  }
  #profit-loss-report-print, #profit-loss-report-print * {
    visibility: visible; border: none;
  }
  .header-position {
    text-align: center;
    font-weight: 700;
    margin-top: -60px !important;
  }
  .table-position {
     margin-top: -60px !important;
  }
  #prints, #prints * {
    visibility:visible;
  }
  #prints {
    position:absolute;
    left:0;
    top:0;
    right: 0;
  }
  img {
  max-width: 50%;
  height: auto;
}
}
</style>
